import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ActivityPageView } from './ActivityPageView';
import { useActivityPage } from './useActivityPage';

/**
 * `/accounting/operations/activity` — everything recorded through this module,
 * in one auditable place.
 *
 * A register of **recorded operations**, not of journal entries filtered by
 * source: the server joins outward from the records, so a hand-keyed entry
 * cannot appear here however its description reads. Each row opens a drawer with
 * the lines it posted, who recorded it, and which frozen template version it
 * posted under — and, when it has been reversed, the linked reversal with its
 * reason.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, because React boundaries do not catch errors thrown by the
 * component that renders them.
 *
 * **The boundary is for the register's read.** An unreachable register renders
 * as no rows, which is indistinguishable from a tenant that has recorded
 * nothing — the two must never converge on the same pixels, so the failed read
 * throws and the genuinely empty register gets its own honest empty state. A
 * failed *drawer* read is the exception and is rendered inside the drawer:
 * throwing there would replace a working register with a full-page error over
 * one row that would not open.
 */
export const ActivityPage: React.FC = () => {
    const page = useActivityPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('operations.activity.error.title'),
            errorMessage: t('operations.activity.error.message'),
            errorRetry: t('operations.activity.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('operations-activity-page')}
        >
            <ErrorBoundary
                data-testid="operations-activity-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="operations-activity-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <ActivityPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type ActivityPageContentProps = {
    page: ReturnType<typeof useActivityPage>;
};

const ActivityPageContent: React.FC<ActivityPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    return (
        <ActivityPageView
            access={page.access}
            canRead={page.canRead}
            canReverse={page.canReverse}
            filters={page.filters}
            isLoading={page.isLoading}
            locale={page.locale}
            onCloseDetail={page.onCloseDetail}
            onKeyChange={page.onKeyChange}
            onPageChange={page.onPageChange}
            onRangeChange={page.onRangeChange}
            onSelect={page.onSelect}
            onStatusChange={page.onStatusChange}
            operations={page.operations}
            pageInfo={page.pageInfo}
            rows={page.rows}
            selectedRecordId={page.selectedRecordId}
        />
    );
};
